import {isFeatureEnabled} from 'feature-flags';

import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';

import {useCanOpenTelephonyCall} from '../../../hooks/useCanOpenTelephonyCall';
import {usePermissions} from '../../../hooks/usePermissions/usePermissions';
import {useTelephonyCallRecordPairing} from '../../../hooks/useTelephonyCallRecordPairing';
import {isTelephonyCallPairingResource} from '../../../utils/isTelephonyCallPairingResource';
import {ThreadCallPairing} from '../components/ThreadCallCard';

interface ThreadCallActionsRecord {
  resourceId: EntityResourceIds;
  recordId: string;
}

/**
 * What a thread on a record lets the viewer do with its calls: pair them with that record (only
 * the four pairable kinds), write their notes and open their detail page. Pairing and notes take the
 * same gate as every other call entry point, the `core_telephony` flag plus `telephonyCallStart`;
 * keep it in step with `useTelephonyCallAvailability` in `@dms/features/telephony-call`, which this
 * lib cannot import. Opening the detail page takes the flag plus `callRead`, as the page itself;
 * playing a recording the flag plus `callListenRecording`, as the call detail's Recording card.
 */
export function useThreadCallActions(record: ThreadCallActionsRecord) {
  const [canStartCall, canListenRecordingPermission] = usePermissions({
    permissionKeys: ['telephonyCallStart', 'callListenRecording'],
  });
  const {pair, unpair, pendingCallId} = useTelephonyCallRecordPairing();
  const canOpenCall = useCanOpenTelephonyCall();

  const isTelephonyAllowed = isFeatureEnabled(featureFlags.CORE_TELEPHONY) && canStartCall;
  const pairingRecord = {resourceId: record.resourceId, recordId: record.recordId};
  const resourceId = record.resourceId;

  const callPairing: ThreadCallPairing | undefined =
    isTelephonyAllowed && isTelephonyCallPairingResource(resourceId)
      ? {
          resourceId,
          recordId: record.recordId,
          pendingCallId,
          onPair: (callId) => pair(callId, pairingRecord),
          onUnpair: (callId) => unpair(callId, pairingRecord),
        }
      : undefined;

  return {
    callPairing,
    canEditCallNotes: isTelephonyAllowed,
    canOpenCall,
    canListenRecording:
      isFeatureEnabled(featureFlags.CORE_TELEPHONY) && canListenRecordingPermission,
  };
}
